import { isPlaceRef } from "@/types/entity"
import type { ItineraryEventType } from "@/types/itinerary"
import type { PlaceSummary } from "@/types/place"
import type {
  VoiceAdditionalToolCall,
  VoiceAudioProfile,
  VoiceChatToolCandidate,
  VoiceLiveEntry,
  VoiceLiveItem,
  VoiceToolResult,
  VoiceTranscriptRole,
} from "@/types/voice-chat"
import {
  VOICE_AUDIO_SYSTEM_DEVICE_ID,
  type ToolOutputDelivery,
  type VoiceFinalizedTranscriptTurn,
  type VoicePlaceAnnotationCandidate,
  type VoiceRealtimeClientEvent,
  type VoiceRealtimeEvent,
  type VoiceRealtimeResponseDiagnostic,
  type VoiceRealtimeResponseInputItem,
  type VoiceResponseCreateTrigger,
  type VoiceSessionStateSnapshot,
} from "@/types/voice-chat-rtc"

const VOICE_TOOL_ARGUMENT_LABEL_MAX_LENGTH = 120
const VOICE_PLACE_ANNOTATION_MAX_CANDIDATES = 50
const VOICE_PLACE_ANNOTATION_FIELD_MAX_LENGTH = 200
const VOICE_PLACE_ANNOTATION_TYPES = [
  "activity",
  "airport",
  "attraction",
  "cafe",
  "camp",
  "event",
  "experience",
  "flight",
  "generic",
  "hotel",
  "location",
  "restaurant",
  "car_rental",
  "transit",
] satisfies ItineraryEventType[]

export const VOICE_PLACE_ANNOTATION_TSV_HEADER =
  "original_text\tname\ttype\tlocation\tproper_noun\tentity_ref\n"
export const VOICE_PLACE_ANNOTATION_TSV_MAX_LENGTH =
  VOICE_PLACE_ANNOTATION_TSV_HEADER.length +
  VOICE_PLACE_ANNOTATION_MAX_CANDIDATES *
    (VOICE_PLACE_ANNOTATION_FIELD_MAX_LENGTH * 6 + 6)

// Tool calls the customer never sees in the voice UI: internal context-gathering
// and quick lookups whose answers surface through the assistant's phrasing, not
// as visible search work. Trace logging and backend persistence are unaffected.
export const UI_HIDDEN_VOICE_TOOL_NAMES = new Set([
  "memory_lookup",
  "conversation_history",
  "convert_currency",
  "place_distance",
])

export const LONG_RUNNING_VOICE_TOOL_NAMES = new Set([
  "hotel_search",
  "transportation_search",
  "event_search",
  "search_travel_alerts",
  "travel_visa_info",
  "search_exhibit",
])

const VOICE_TIMELINE_BOTTOM_THRESHOLD_PX = 48

export function voiceTimelineAtBottom(viewport: HTMLElement) {
  return (
    viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <=
    VOICE_TIMELINE_BOTTOM_THRESHOLD_PX
  )
}

// How far (px) the caption should scroll to sit at its anchor — positive
// means scrolling down. The anchor pins the reveal frontier (the spoken/
// not-yet-spoken boundary) to the caption's vertical center: the line being
// spoken sits as the middle line, spoken context above, dim remainder below.
// Placing its BOTTOM half a line-height below the midpoint is what centers
// the line itself. Best-effort at the edges, where the browser clamps the
// scroll. Falls back to the bottom when nothing is actively revealing.
export function voiceTimelineAnchorDelta(
  viewport: HTMLElement,
  frontier: HTMLElement | null
) {
  if (!frontier) {
    return (
      Math.max(0, viewport.scrollHeight - viewport.clientHeight) -
      viewport.scrollTop
    )
  }
  return voiceFrontierAnchorDelta(viewport, frontier)
}

// Mid-reveal the anchor sits ABOVE the true bottom (the dim tail hangs below
// the centered frontier), so callers must judge "caught up" against the
// anchor, not the bottom.
function voiceFrontierAnchorDelta(
  viewport: HTMLElement,
  frontier: HTMLElement
) {
  const viewportRect = viewport.getBoundingClientRect()
  const frontierRect = frontier.getBoundingClientRect()
  const lineHeight = parseFloat(getComputedStyle(frontier).lineHeight) || 0
  return (
    frontierRect.bottom -
    (viewportRect.top + viewportRect.height / 2 + lineHeight / 2)
  )
}

export function parseVoiceRealtimeEvent(
  raw: string
): VoiceRealtimeEvent | null {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isVoiceRealtimeEvent(parsed)) return null

    return parsed
  } catch (error) {
    if (error instanceof SyntaxError) return null
    throw error
  }
}

function isVoiceRealtimeEvent(parsed: unknown): parsed is VoiceRealtimeEvent {
  return (
    typeof parsed === "object" &&
    parsed !== null &&
    "type" in parsed &&
    typeof parsed.type === "string"
  )
}

export type VoiceTurnTransition =
  | { kind: "turnStarted"; id: string; role: VoiceTranscriptRole }
  | { kind: "userTranscriptDelta"; id?: string; delta: string }
  | {
      kind: "assistantTranscriptDelta"
      id: string
      responseId: string
      delta: string
    }
  | {
      kind: "turnCompleted"
      id: string
      role: VoiceTranscriptRole
      text: string
    }
  | { kind: "turnInterrupted"; id: string; audioEndMs: number }
  // Pause-cut bookkeeping, dispatched by the RTC directly rather than
  // normalized from a provider event. The reveal freezes at the word boundary
  // the resume will continue from, but the text KEEPS the unspoken remainder —
  // the caption's dimmed tail must survive the pause. `text` is the session's
  // whole pending speech (the paused item plus any gapless successors folded
  // in), so the one row shows everything the resume will say.
  | { kind: "turnPaused"; id: string; heardLength: number; text: string }
  // A pause-resume continuation reopens the paused turn: its deltas append to
  // this item instead of opening a new one (see VoiceChatRTC.setPaused).
  // `heardLength` is the pause cut — the prefix the continuation's audio
  // window never speaks, excluded from that window's pacing and metering.
  | { kind: "turnResumed"; id: string; heardLength: number }
  // The customer spoke while paused, which supersedes the held remainder: the
  // pause ends here rather than at the resume button, and the paused row's
  // dimmed tail is trimmed to `heardLength` because those words will never be
  // spoken now. `id` is null when the pause had cut nothing off (a pause taken
  // while the assistant was already quiet) — the store's paused flag still
  // clears.
  | { kind: "pauseAbandoned"; id: string | null; heardLength: number }
  | { kind: "audioStarted"; responseId: string }
  | { kind: "audioStopped"; responseId: string }
  | { kind: "audioCleared"; responseId: string }
  | {
      kind: "responseStarted"
      responseId: string
      trigger?: VoiceResponseCreateTrigger
    }
  | {
      kind: "assistantItemAdded"
      id: string
      responseId: string
      role?: VoiceTranscriptRole
      itemType: string
    }
  | { kind: "responseFinished"; responseId: string | null }
  | { kind: "userSpeechStarted" }
  | { kind: "sessionError"; message: string }

// This is the provider-event boundary: normalize only the event types that
// mutate voice session state, leaving ordering and persistence authority to the
// RTC orchestrator that consumes these transitions.
export function toTurnTransitions(
  event: VoiceRealtimeEvent
): VoiceTurnTransition[] {
  if (
    event.type === "conversation.item.created" ||
    event.type === "conversation.item.added" ||
    event.type === "conversation.item.done"
  ) {
    if (event.item.id.startsWith("mindtrip_history_")) return []
    if (event.item.id.startsWith("item_mindtrip_tool_media_")) return []
    if (event.item.type !== "message" || !event.item.role) return []
    if (event.item.role !== "user" && event.item.role !== "assistant") return []
    return [{ kind: "turnStarted", id: event.item.id, role: event.item.role }]
  }

  if (event.type === "conversation.item.input_audio_transcription.delta") {
    return [
      { kind: "userTranscriptDelta", id: event.item_id, delta: event.delta },
    ]
  }

  if (event.type === "conversation.item.input_audio_transcription.completed") {
    return [
      {
        kind: "turnCompleted",
        id: event.item_id,
        role: "user",
        text: event.transcript,
      },
    ]
  }

  if (event.type === "response.output_audio_transcript.delta") {
    return [
      {
        kind: "assistantTranscriptDelta",
        id: event.item_id,
        responseId: event.response_id,
        delta: event.delta,
      },
    ]
  }

  if (event.type === "response.output_audio_transcript.done") {
    return [
      {
        kind: "turnCompleted",
        id: event.item_id,
        role: "assistant",
        text: event.transcript,
      },
    ]
  }

  if (event.type === "output_audio_buffer.started") {
    return [{ kind: "audioStarted", responseId: event.response_id }]
  }

  if (event.type === "output_audio_buffer.stopped") {
    return [{ kind: "audioStopped", responseId: event.response_id }]
  }

  if (event.type === "output_audio_buffer.cleared") {
    return [{ kind: "audioCleared", responseId: event.response_id }]
  }

  if (event.type === "input_audio_buffer.speech_started") {
    return [{ kind: "userSpeechStarted" }]
  }

  if (event.type === "response.created") {
    const trigger = event.response.metadata?.mindtrip_trigger
    return [
      {
        kind: "responseStarted",
        responseId: event.response.id,
        ...(trigger ? { trigger } : {}),
      },
    ]
  }

  if (event.type === "response.output_item.added") {
    return [
      {
        kind: "assistantItemAdded",
        id: event.item.id,
        responseId: event.response_id,
        role:
          event.item.role === "user" || event.item.role === "assistant"
            ? event.item.role
            : undefined,
        itemType: event.item.type,
      },
    ]
  }

  if (event.type === "conversation.item.truncated") {
    return [
      {
        kind: "turnInterrupted",
        id: event.item_id,
        audioEndMs: event.audio_end_ms,
      },
    ]
  }

  if (event.type === "response.done") {
    return [
      { kind: "responseFinished", responseId: event.response?.id ?? null },
    ]
  }

  if (event.type === "error") {
    return [
      {
        kind: "sessionError",
        message:
          event.message ?? event.error?.message ?? "Voice session failed",
      },
    ]
  }

  return []
}

// Realtime `error` events are almost always the provider rejecting one client
// event (a cancel with no active response, a truncate of an item already gone)
// while the session itself keeps running — so an unrecognized error must NOT
// end the surface. Only codes that mean the session as a whole is over are
// fatal; everything else is traced and swallowed by the RTC before it can
// become a `sessionError` transition.
const FATAL_REALTIME_ERROR_CODES = new Set(["session_expired"])

export function isFatalRealtimeError(
  event: Extract<VoiceRealtimeEvent, { type: "error" }>
) {
  const code = event.error?.code ?? event.code
  return code !== undefined && FATAL_REALTIME_ERROR_CODES.has(code)
}

// The one constraint builder for every voice getUserMedia request (voice chat's
// open flow, its start retry and reconnect, and the OpenAI interview), so every
// acquisition honors the server's audio profile and the customer's device choice.
// Everything but the device is `ideal`: a browser that does not know
// voiceIsolation or cannot meet a preference ignores it instead of failing the
// whole request the way an `exact` constraint would.
export function voiceMicConstraints(
  microphone: VoiceAudioProfile["microphone"],
  selectedInputDeviceId: string
): MediaTrackConstraints {
  // voiceIsolation is in the Media Capture extensions, not the installed DOM lib.
  const constraints: MediaTrackConstraints & {
    voiceIsolation?: ConstrainBoolean
  } = {
    echoCancellation: microphone.echo_cancellation,
    noiseSuppression: microphone.noise_suppression,
    autoGainControl: microphone.auto_gain_control,
    voiceIsolation: { ideal: microphone.voice_isolation },
  }
  if (microphone.channel_count !== undefined) {
    constraints.channelCount = { ideal: microphone.channel_count }
  }
  if (selectedInputDeviceId !== VOICE_AUDIO_SYSTEM_DEVICE_ID) {
    constraints.deviceId = { exact: selectedInputDeviceId }
  }
  return constraints
}

export function realtimeEventResponseId(event: VoiceRealtimeEvent) {
  if ("response_id" in event) return event.response_id
  if (event.type === "response.created") return event.response.id
  if (event.type === "response.done") return event.response?.id ?? null
  return null
}

export function realtimeEventItemId(event: VoiceRealtimeEvent) {
  if ("item_id" in event) return event.item_id
  if (
    event.type === "conversation.item.created" ||
    event.type === "conversation.item.added" ||
    event.type === "conversation.item.done"
  ) {
    return event.item.id
  }
  if (event.type === "response.output_item.added") return event.item.id
  return null
}

function isToolArguments(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function toolArgumentsFromRealtimeEvent(
  argumentsText: string
): Record<string, unknown> {
  const parsed: unknown = JSON.parse(argumentsText)
  if (!isToolArguments(parsed)) {
    throw new Error("Tool call arguments must be an object.")
  }

  return parsed
}

export function voicePlaceAnnotationClientEvent(
  eventId: string,
  providerEventId: string,
  input: VoiceRealtimeResponseInputItem[],
  validationError?: string
): VoiceRealtimeClientEvent {
  const validationFeedback = validationError
    ? ` The previous response failed validation with this exact error: ${validationError} Return the complete corrected TSV.`
    : ""

  return {
    type: "response.create",
    event_id: eventId,
    response: {
      conversation: "none",
      input: [
        ...input,
        {
          type: "message",
          role: "user",
          content: [
            {
              type: "input_text",
              text: "Extract place mentions from the immediately preceding assistant message. Return only the required TSV.",
            },
          ],
        },
      ],
      output_modalities: ["text"],
      instructions: [
        "# TASK",
        "- Analyze only the final completed assistant message in the conversation context.",
        "- Earlier conversation and verified tool context may identify or disambiguate a place explicitly mentioned in the final message, including supplying its geography, but must never create a mention absent from that message.",
        "- Scan the entire final message and extract every eligible explicit named-place mention, including mentions in opening or setup language, progress or status sentences, transitions, recommendations, summaries, and closing language. A mention's conversational role never makes it skippable.",
        "# ELIGIBILITY",
        "- Eligible places include specific hotels, restaurants or cafes, attractions, airports, named venues, and named locations such as neighborhoods, cities, islands, regions, states, or countries.",
        `- Each row must denote one concrete place. Separate coordinated place names into separate rows. Use only these type values: ${VOICE_PLACE_ANNOTATION_TYPES.join(", ")}. The type vocabulary classifies eligible places; it does not make events, flights, people, non-place organizations, services, generic activities, or generic categories eligible.`,
        "- Cover every eligible mention, but emit one row per unique original_text and intended identity pair rather than one row per occurrence. Rails expands one row to every identical bounded occurrence.",
        "- Do not emit truncated or nested alternative boundaries for the same intended occurrence. Emit separately spoken aliases with different original_text values as separate rows.",
        "- If identical occurrences of one original_text clearly denote different place identities, omit rows for that phrase because this contract has no offsets; continue returning unrelated eligible places.",
        "- Treat a bare IATA code as a place when it functions as an airport or flight route in the final message. Preserve the exact code as original_text and use airport as type.",
        "- Treat a non-airport abbreviation as eligible only when the final message makes the abbreviation itself function as an explicit specific named place, including a contextual city abbreviation or established place alias. Exclude bare state, country, organization, service, and other abbreviations that do not function as specific named places.",
        "- Exclude generic categories, vague area hints, people, non-place organizations, and places inferred only from earlier conversation or trip context.",
        "# FIELDS",
        "- Distinguish uncertain identity from an uncertain source span. If a source phrase clearly names a place but its identity remains uncertain after using allowed context, still emit it by preserving the source identity in name and leaving unsupported location and entity_ref fields empty. Omit a phrase only when its exact place-name boundary cannot be determined.",
        "- Copy the exact complete contiguous name-bearing words from the final message into original_text. Preserve the source wording, exclude only adjacent words that are not part of the name, and never truncate a multiword name.",
        "- For a clearly identified established alias, contextual abbreviation, exonym, or local-language or cross-script name, preserve the exact source form in original_text and use the full canonical place identity in name. Otherwise preserve the named source identity rather than inventing disambiguation.",
        "- Populate location only when the final message, earlier conversation, or verified tool context clearly establishes relevant city, region, state, or country geography for that explicit final-message mention. Otherwise leave location empty.",
        "- Set proper_noun from the phrase's contextual use as a specific named place, not from capitalization alone.",
        "- Populate entity_ref only when verified tool context identifies the exact place denoted by that mention; otherwise leave the column empty. Never invent or transform an entity ref.",
        "# OUTPUT",
        "- Return the header alone only when no eligible explicit named-place row remains under these rules.",
        '- Example: for the final message "I want to visit the MET in New York, do some shopping, then return to New York," preserve the MET alias in original_text, use Metropolitan Museum of Art as its canonical name, exclude shopping, and emit one New York row because Rails expands it to both occurrences.',
        `- The complete TSV for that example is:\n${VOICE_PLACE_ANNOTATION_TSV_HEADER}MET\tMetropolitan Museum of Art\tattraction\tNew York, New York\ttrue\t\nNew York\tNew York\tlocation\tNew York, New York\ttrue\t\n`,
        `- RETURN STRICT TSV beginning with exactly this header: ${VOICE_PLACE_ANNOTATION_TSV_HEADER.trimEnd()}. Follow it with zero to 50 newline-terminated rows. Every row must contain exactly six tab-separated columns in header order. Use an empty column for an absent location or entity_ref. Do not add markdown, prose, quoting, escaping, or blank lines. Each field is limited to ${VOICE_PLACE_ANNOTATION_FIELD_MAX_LENGTH} characters.${validationFeedback}`,
      ].join("\n"),
      tools: [],
      metadata: {
        mindtrip_trigger: "place_annotation",
        provider_event_id: providerEventId,
      },
    },
  }
}

function isVoicePlaceAnnotationRow(
  fields: string[]
): fields is [string, string, string, string, string, string] {
  return fields.length === 6
}

export function parseVoicePlaceAnnotationTsv(
  document: string
): VoicePlaceAnnotationCandidate[] {
  if (document.length > VOICE_PLACE_ANNOTATION_TSV_MAX_LENGTH) {
    throw new Error(
      `Place annotation document length exceeds the limit: expected at most ${VOICE_PLACE_ANNOTATION_TSV_MAX_LENGTH}, got ${document.length}.`
    )
  }
  const headerStart = document.indexOf(VOICE_PLACE_ANNOTATION_TSV_HEADER)
  if (headerStart === -1) {
    throw new Error(
      "Place annotation document is missing the exact TSV header."
    )
  }

  let normalizedDocument = document.slice(headerStart)
  if (normalizedDocument.endsWith("\n```\n")) {
    normalizedDocument = normalizedDocument.slice(0, -4)
  }
  if (!normalizedDocument.endsWith("\n")) {
    throw new Error("Place annotation document must end with a newline.")
  }
  if (normalizedDocument.includes("\r") || /["\\\\]/.test(normalizedDocument)) {
    throw new Error("Place annotation document contains forbidden framing.")
  }

  const lines = normalizedDocument.slice(0, -1).split("\n").slice(1)
  if (lines.length > VOICE_PLACE_ANNOTATION_MAX_CANDIDATES) {
    throw new Error(
      `Place annotation candidate count exceeds the limit: expected at most ${VOICE_PLACE_ANNOTATION_MAX_CANDIDATES}, got ${lines.length}.`
    )
  }

  return lines.map((line, index) => {
    const rowNumber = index + 1
    const fields = line.split("\t")
    // Realtime models may omit delimiters for trailing empty TSV fields. Padding
    // preserves those blanks while required-field validation rejects incomplete rows.
    while (fields.length < 6) {
      fields.push("")
    }
    if (!isVoicePlaceAnnotationRow(fields)) {
      throw new Error(
        `Place annotation row ${rowNumber} must contain six columns: expected 6, got ${fields.length}.`
      )
    }
    const [originalText, name, type, location, properNounText, entityRef] =
      fields
    if (
      [originalText, name, type, location, properNounText, entityRef].some(
        (field) => field.length > VOICE_PLACE_ANNOTATION_FIELD_MAX_LENGTH
      )
    ) {
      throw new Error(
        `Place annotation row ${rowNumber} has a field exceeding the ${VOICE_PLACE_ANNOTATION_FIELD_MAX_LENGTH}-character limit.`
      )
    }
    if (originalText.trim().length === 0 || name.trim().length === 0) {
      throw new Error(`Place annotation row ${rowNumber} has invalid identity.`)
    }
    if (
      !VOICE_PLACE_ANNOTATION_TYPES.some(
        (candidateType) => candidateType === type
      )
    ) {
      throw new Error(`Place annotation row ${rowNumber} has an invalid type.`)
    }
    if (properNounText !== "true" && properNounText !== "false") {
      throw new Error(
        `Place annotation row ${rowNumber} has an invalid proper-noun flag.`
      )
    }
    const candidate: VoicePlaceAnnotationCandidate = {
      originalText,
      name,
      type,
      properNoun: properNounText === "true",
    }
    if (location.length > 0) {
      candidate.location = location
    }
    if (isPlaceRef(entityRef)) {
      candidate.entityRef = entityRef
    }
    return candidate
  })
}

export function toolCandidatesFromResult(
  result: VoiceToolResult
): VoiceChatToolCandidate[] {
  if ("status" in result || !result.entityMap) return []

  const candidates = Object.entries(result.entityMap).flatMap(
    ([name, entityRefs]) =>
      entityRefs.filter(isPlaceRef).map((entityId) => ({ name, entityId }))
  )

  return candidates.map((candidate, index) => ({
    entityId: candidate.entityId,
    type: "place",
    name: candidate.name,
    rank: index + 1,
  }))
}

// Bookable hotel quotes the answer created, in the order the agent presented
// them, extracted server-side from the result content (VoiceToolResults) and
// delivered as a typed field. A ref is all the client needs:
// GET /api/price_quotes/:ref returns the room, dates, occupancy and price.
export function hotelQuoteRefsFromResult(result: VoiceToolResult): string[] {
  if ("status" in result) return []

  return result.hotelQuoteRefs ?? []
}

export function voiceToolResultTerminal(result: VoiceToolResult) {
  return !("status" in result && result.status === "running")
}

function voiceToolOutputPayload(result: VoiceToolResult) {
  if ("status" in result) return result

  const payload = {
    results: result.speechResults ?? result.results,
    isError: result.isError,
    entityMap: result.entityMap,
    error: result.error,
  }

  return Object.fromEntries(
    Object.entries(payload).filter((entry) => entry[1] !== undefined)
  )
}

export function voiceToolOutputClientEvent(
  itemId: string,
  delivery: ToolOutputDelivery,
  eventId: string,
  previousItemId?: string
): VoiceRealtimeClientEvent {
  const { result } = delivery
  const payload =
    "isError" in result && result.isError
      ? result
      : voiceToolOutputPayload(result)
  const output = JSON.stringify(payload)
  return voiceFunctionCallOutputClientEvent(
    eventId,
    itemId,
    delivery.callId,
    output,
    previousItemId
  )
}

export function voiceFunctionCallOutputClientEvent(
  eventId: string,
  itemId: string,
  callId: string,
  output: string,
  previousItemId?: string
): VoiceRealtimeClientEvent {
  return {
    type: "conversation.item.create",
    event_id: eventId,
    previous_item_id: previousItemId,
    item: {
      type: "function_call_output",
      id: itemId,
      call_id: callId,
      output,
    },
  }
}

// Realtime carries function-call arguments as a JSON string, while the backend
// snapshot payload keeps them as an object.
export function voiceFunctionCallClientEvent(
  eventId: string,
  itemId: string,
  call: VoiceAdditionalToolCall,
  previousItemId?: string
): VoiceRealtimeClientEvent {
  return {
    type: "conversation.item.create",
    event_id: eventId,
    previous_item_id: previousItemId,
    item: {
      type: "function_call",
      id: itemId,
      call_id: call.callId,
      name: call.name,
      arguments: JSON.stringify(call.arguments),
    },
  }
}

export function voiceToolMediaClientEvent(
  eventId: string,
  itemId: string,
  media: Array<{ dataUri: string }>,
  previousItemId?: string
): VoiceRealtimeClientEvent {
  const content: Array<{
    type: "input_image"
    image_url: string
    detail: "high"
  }> = media.map(({ dataUri }) => ({
    type: "input_image",
    image_url: dataUri,
    detail: "high",
  }))
  return {
    type: "conversation.item.create",
    event_id: eventId,
    previous_item_id: previousItemId,
    item: {
      type: "message",
      id: itemId,
      role: "user",
      content,
    },
  }
}

// Identifies a finalized transcript candidate at the provider boundary. The RTC
// still chooses persistence authority: assistant text waits for audio drain or
// takes an explicit interruption, no-audio, or teardown terminal path.
export function finalizedTranscriptTurnForRealtimeEvent(
  event: VoiceRealtimeEvent,
  state: VoiceSessionStateSnapshot
): VoiceFinalizedTranscriptTurn | null {
  if (event.type === "conversation.item.input_audio_transcription.completed") {
    if (event.transcript.trim().length === 0) return null

    return {
      role: "user",
      transcript: event.transcript,
      providerEventId: event.item_id,
      interrupted: false,
    }
  }

  if (event.type === "response.output_audio_transcript.done") {
    if (event.transcript.trim().length === 0) return null
    if (
      voiceLiveItemEntries(state).find(
        (entry) => entry.item.itemId === event.item_id
      )?.item.interrupted
    ) {
      return null
    }

    return {
      role: "assistant",
      transcript: event.transcript,
      providerEventId: event.item_id,
      interrupted: false,
    }
  }

  return null
}

export function liveItemVisibleText(item: VoiceLiveItem) {
  if (item.role === "user") return item.text
  return item.text.slice(0, item.revealedLength)
}

export function voiceLiveItemEntries(
  state: Pick<VoiceSessionStateSnapshot, "liveStream">
) {
  return state.liveStream.filter(
    (entry): entry is Extract<VoiceLiveEntry, { kind: "item" }> =>
      entry.kind === "item"
  )
}

// Reveal-gated places of one live item: only places whose mention the caption
// has already revealed contribute a card, so the carousel tracks the spoken
// transcript instead of leading it. Fully played turns snap fully revealed, so
// this only gates the turn currently being spoken.
export function revealedItemPlaces(item: VoiceLiveItem): PlaceSummary[] {
  const revealedPlaceRefs = new Set(
    (item.markers ?? []).flatMap((marker) => {
      if (
        (marker.type === "place" || marker.type === "card") &&
        marker.ref !== undefined &&
        marker.end <= item.revealedLength
      ) {
        return [marker.ref]
      }
      return []
    })
  )
  return (item.places ?? []).filter((place) =>
    revealedPlaceRefs.has(place.entity_ref)
  )
}

export function voiceLiveToolEntries(
  state: Pick<VoiceSessionStateSnapshot, "liveStream">
) {
  return state.liveStream.filter(
    (entry): entry is Extract<VoiceLiveEntry, { kind: "tool" }> =>
      entry.kind === "tool"
  )
}

export function toolArgumentsLabel(
  toolName: string,
  argumentsValue: Record<string, unknown>
) {
  const query = argumentsValue.query
  if (toolName === "transportation_search" && typeof query === "string") {
    return query
  }

  const from = argumentsValue.from
  const to = argumentsValue.to
  if (
    toolName === "transportation_search" &&
    typeof from === "string" &&
    typeof to === "string"
  ) {
    const type = argumentsValue.type
    return typeof type === "string"
      ? `${from} to ${to} - ${type}`
      : `${from} to ${to}`
  }

  const names = argumentsValue.names
  if (Array.isArray(names)) {
    const label = names
      .filter((name): name is string => typeof name === "string")
      .join(", ")
    if (label.length > 0) return label
  }

  if (typeof query === "string" && query.length > 0) return query

  const location = argumentsValue.location
  if (typeof location === "string" && location.length > 0) return location

  const rawLabel = JSON.stringify(argumentsValue)
  if (rawLabel.length <= VOICE_TOOL_ARGUMENT_LABEL_MAX_LENGTH) return rawLabel

  return `${rawLabel.slice(0, VOICE_TOOL_ARGUMENT_LABEL_MAX_LENGTH - 3)}…`
}

export function toolLocationLabel(argumentsValue: Record<string, unknown>) {
  const location = argumentsValue.location
  return typeof location === "string" && location.length > 0
    ? location
    : undefined
}

export function voiceRealtimeDebugState(state: VoiceSessionStateSnapshot) {
  const liveItemEntries = voiceLiveItemEntries(state)

  return {
    connectionStatus: state.connectionStatus,
    activeAssistantResponseId: state.activeAssistantResponseId,
    audibleAssistantResponseId: state.audibleAssistantResponseId,
    activeAssistantItemId: state.activeAssistantItemId,
    items: liveItemEntries.map(({ item }) => ({
      itemId: item.itemId,
      role: item.role,
      status: item.status,
      textLen: item.text.length,
      revealedLen: item.revealedLength,
      persistStatus: item.persistenceStatus,
    })),
    unrevealedAssistantLength: liveItemEntries.reduce(
      (length, entry) =>
        entry.item.role === "assistant"
          ? length + entry.item.text.length - entry.item.revealedLength
          : length,
      0
    ),
  }
}

function voiceRealtimeResponseDiagnostic(
  event: Extract<
    VoiceRealtimeEvent,
    { type: "response.created" | "response.done" }
  >
): VoiceRealtimeResponseDiagnostic {
  const response = event.response
  const outputItems = (response?.output ?? []).map((item) => ({
    id: item.id,
    type: item.type,
    role:
      item.role === "user" ||
      item.role === "assistant" ||
      item.role === "system"
        ? item.role
        : null,
  }))

  return {
    providerEventId: event.event_id ?? null,
    responseId: response?.id ?? null,
    status: response?.status ?? null,
    statusDetails: response?.status_details ?? null,
    metadata: response?.metadata ?? null,
    outputItems,
    outputItemCount: outputItems.length,
    inputTokens: response?.usage?.input_tokens ?? null,
    outputTokens: response?.usage?.output_tokens ?? null,
    cachedInputTokens:
      response?.usage?.input_token_details?.cached_tokens ?? null,
    textInputTokens: response?.usage?.input_token_details?.text_tokens ?? null,
    audioInputTokens:
      response?.usage?.input_token_details?.audio_tokens ?? null,
  }
}

export function voiceRealtimeDebugEvent(event: VoiceRealtimeEvent) {
  if (event.type === "response.created" || event.type === "response.done") {
    return {
      type: event.type,
      response: voiceRealtimeResponseDiagnostic(event),
    }
  }

  if (event.type === "error") {
    return {
      type: event.type,
      message: event.message ?? event.error?.message ?? null,
      code: event.code ?? event.error?.code ?? null,
      errorType: event.error?.type ?? null,
    }
  }

  if (event.type === "response.output_text.delta") {
    return {
      type: event.type,
      responseId: realtimeEventResponseId(event),
      itemId: realtimeEventItemId(event),
      delta: event.delta,
    }
  }

  if (
    event.type === "conversation.item.input_audio_transcription.delta" ||
    event.type === "response.output_audio_transcript.delta"
  ) {
    return {
      type: event.type,
      responseId: realtimeEventResponseId(event),
      itemId: realtimeEventItemId(event),
      deltaLength: event.delta.length,
    }
  }

  if (
    event.type === "conversation.item.input_audio_transcription.completed" ||
    event.type === "response.output_audio_transcript.done"
  ) {
    return {
      type: event.type,
      responseId: realtimeEventResponseId(event),
      itemId: realtimeEventItemId(event),
      transcriptLength: event.transcript.length,
    }
  }

  if (event.type === "conversation.item.input_audio_transcription.failed") {
    return {
      type: event.type,
      itemId: realtimeEventItemId(event),
      message: event.error?.message ?? null,
      code: event.error?.code ?? null,
      errorType: event.error?.type ?? null,
    }
  }

  return {
    type: event.type,
    responseId: realtimeEventResponseId(event),
    itemId: realtimeEventItemId(event),
  }
}

export function voiceClockNow() {
  return performance.now()
}
